import React, { useEffect, useState } from 'react';
import { Card, Button, Table, Row, Col } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import styles from './SummaryViewer.module.less';
import SummaryViewerService from './SummaryViewer.service';

interface SummaryViewerProps {
  projectId: number;
}

interface FireSafetyData {
    project_id: number;
    buildings: BuildingData[];
    overall_status: 'safe' | 'warning' | 'danger';
    total_issues: number;
    buildings_count: number;
}

interface BuildingData {
    building_name: string;
    summary: {
        safety_exits: {
            total: number;
            by_floor: { floor: string; count: number }[];
            is_sufficient: boolean;
            insufficient_floors: string[];
        };
        evacuation_doors: { total: number };
        staircases: { total: number; types: string[] };
        antechambers: {
            total: number;
            short_side_lengths: number[];
            min_length: number | null;
            max_length: number | null;
            is_compliant: boolean;
            non_compliant_lengths: number[];
            non_compliant_antechambers?: { short_side_length: number; issue: string }[];
        };
        fire_compartments: { count: number; total_area: number };
        elevator_rooms: { total: number; fire_elevator_rooms: number };
        fire_elevators: { count: number; has_fire_elevator: boolean; issue: string | null };
        rescue_windows: { count: number; has_rescue_window: boolean; issue: string | null };
        fire_windows: { count: number; has_fire_window: boolean };
        doors: { total: number; fire_doors: { total: number; grade_a: number; grade_b: number; grade_c: number } };
        fire_protection: {
            hydrant_protected_rooms: { single_point: number; double_point: number; total: number };
            extinguisher_protected_rooms: number;
            fire_hazard_rooms: { class_a: number; class_b: number; total: number };
        };
        occupant_density: { is_crowded_place: string };
        building_info: {
            total_floors: number;
            building_height_above: number | null;
            building_height_below: number | null;
            fire_resistance_rating: string | null;
            total_building_area: number;
            smoke_detector: string | null;
        };
        extinguisher_count: number;
        hydrant_count: number;
        smoke_detector_count: number;
        overall_issues: string[];
        overall_status: 'safe' | 'warning' | 'danger';
        layout_overview?: {
            total_rooms: number;
            exit_distribution: string[];
            stair_distribution: string[];
            hydrant_coverage_rate: number;
            extinguisher_coverage_rate: number;
            has_fire_hazard_rooms: boolean;
        };
    };
    floors_detail: any[];
}

function SummaryViewer({ projectId }: SummaryViewerProps) {
    const [data, setData] = useState<FireSafetyData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        SummaryViewerService.getFireSafetySummary(projectId)
            .then(res => setData(res.data))
            .catch(err => console.error('获取项目摘要失败', err))
            .finally(() => setLoading(false));
    }, [projectId]);

    if (loading) return <div className={styles.root}>项目摘要加载中...</div>;
    if (!data || data.buildings.length === 0) return <div className={styles.root}>暂无消防数据</div>;

    return (
        <div className={styles.root}>
            {data.buildings.map((building, idx) => (
                <FireSafetyCard key={idx} building={building} />
            ))}
        </div>
    );
}

function FireSafetyCard({ building }: { building: BuildingData }) {
    const { summary, floors_detail } = building;
    const layout = summary.layout_overview || {
        total_rooms: 0,
        hydrant_coverage_rate: 0,
        extinguisher_coverage_rate: 0,
        has_fire_hazard_rooms: false,
    };
    const buildingInfo = summary.building_info || {};

    // 建筑高度数值
    let buildingHeight: number | null = null;
    if (buildingInfo.building_height_above != null) {
        const raw = buildingInfo.building_height_above;
        const num = typeof raw === 'number' ? raw : parseFloat(String(raw));
        buildingHeight = isNaN(num) ? null : num;
    }

    // 消防电梯异常
    const getFireElevatorIssue = () => {
        const hasElevator = summary.fire_elevators.count > 0;
        if (hasElevator) return null;
        if (buildingHeight !== null) {
            if (buildingHeight > 33) {
                return `建筑高度${buildingHeight}m超过33m，但未检测到消防电梯，不符合规范要求`;
            } else {
                return null;
            }
        } else {
            return summary.fire_elevators.issue || "未检测到消防电梯，请确认建筑高度是否超过33m";
        }
    };
    const fireElevatorIssue = getFireElevatorIssue();

    // 安全出口不足的楼层
    const insufficientExits = summary.safety_exits.insufficient_floors.map(floor => {
        const floorData = summary.safety_exits.by_floor.find(f => f.floor === floor);
        return `${floor}层（${floorData?.count || 0}个）`;
    });

    // 前室短边不足的楼层
    const nonCompliantAntechambers = summary.antechambers.non_compliant_antechambers || [];
    const antechamberIssues = nonCompliantAntechambers.map(item => {
        const floorWithLength = floors_detail.find(floor =>
            floor.antechambers?.short_side_lengths?.some((len: number) => Math.abs(len - item.short_side_length) < 0.01)
        );
        const floorName = floorWithLength?.floor || '未知楼层';
        return `${floorName}层（${item.short_side_length.toFixed(1)}m）`;
    });

    // 建筑级别保护房间总数与覆盖率
    const totalRooms = layout.total_rooms;
    const hydrantProtectedTotal = summary.fire_protection.hydrant_protected_rooms.total;
    const hydrantCoverage = layout.hydrant_coverage_rate;
    const extinguisherProtectedTotal = summary.fire_protection.extinguisher_protected_rooms;
    const extinguisherCoverage = layout.extinguisher_coverage_rate;

    // 计算地上地下层数
    const getFloorNumber = (name: string): number | null => {
        const match = name.match(/-?\d+/);
        if (match) return parseInt(match[0], 10);
        return null;
    };
    const floorNames = floors_detail.map(f => f.floor).filter(Boolean);
    const aboveCount = floorNames.filter(name => {
        const num = getFloorNumber(name);
        return num !== null && num > 0;
    }).length;
    const belowCount = floorNames.filter(name => {
        const num = getFloorNumber(name);
        return num !== null && num < 0;
    }).length;

    // 构建表格数据（先映射，再按楼层数字排序）
    const rawTableData = floors_detail.map((floor: any, index: number) => {
        const floorRooms = floor.total_rooms ?? 0;
        const extinguisherProtected = floor.fire_protection?.extinguisher ?? 0;
        const hydrantSingle = floor.fire_protection?.hydrant_single ?? 0;
        const hydrantDouble = floor.fire_protection?.hydrant_double ?? 0;
        const hydrantProtected = hydrantSingle;

        return {
            key: index,
            floor: floor.floor || `第${index + 1}层`,
            antechamberCount: floor.antechambers?.total ?? 0,
            totalRooms: floorRooms,
            extinguisherProtected: extinguisherProtected,
            hydrantProtected: hydrantProtected,
            classARooms: floor.fire_hazard_rooms?.class_a ?? 0,
            classBRooms: floor.fire_hazard_rooms?.class_b ?? 0,
            hydrantSingle: hydrantSingle,
            hydrantDouble: hydrantDouble,
            staircases: floor.staircases?.total ?? 0,
            elevatorRooms: floor.elevator_rooms ?? 0,
            fireElevatorRooms: floor.fire_elevator_rooms ?? 0,
            rescueWindows: floor.rescue_windows ?? 0,
            fireWindows: floor.fire_windows ?? 0,
            totalDoors: floor.total_doors ?? 0,
            fireDoorA: floor.fire_doors?.grade_a ?? 0,
            fireDoorB: floor.fire_doors?.grade_b ?? 0,
            fireDoorC: floor.fire_doors?.grade_c ?? 0,
            safetyExits: floor.safety_exits ?? 0,
            evacuationDoors: floor.evacuation_doors ?? 0,
            fireCompArea: floor.fire_compartments?.total_area ?? 0,
            smokeDetectors: floor.smoke_detector_count ?? 0,
            extinguisherCount: floor.extinguisher_count ?? 0,
            hydrantCount: floor.hydrant_count ?? 0,
        };
    });

    // 按楼层数字排序（升序：负楼层 → 正楼层）
    const tableData = rawTableData.sort((a, b) => {
        const numA = getFloorNumber(a.floor) ?? 0;
        const numB = getFloorNumber(b.floor) ?? 0;
        return numA - numB;
    });

    // 每层详细表格列定义
    const columns: ColumnsType<any> = [
        { title: '楼层', dataIndex: 'floor', key: 'floor', width: 80, fixed: 'left', render: (text: string) => text ? `${text}层` : '-' },
        { title: '安全出口', dataIndex: 'safetyExits', key: 'safetyExits', width: 80 },
        { title: '防火分区面积', dataIndex: 'fireCompArea', key: 'fireCompArea', width: 110 },
        { title: '前室数量', dataIndex: 'antechamberCount', key: 'antechamberCount', width: 90 },
        { title: '房间总数', dataIndex: 'totalRooms', key: 'totalRooms', width: 90 },
        {
            title: '灭火器保护房间',
            dataIndex: 'extinguisherProtected',
            key: 'extinguisherProtected',
            width: 150,
            render: (text: number, record: any) => {
                const rate = record.totalRooms > 0 ? ((text / record.totalRooms) * 100).toFixed(1) : '0';
                return `${text} (${rate}%)`;
            }
        },
        {
            title: '消火栓保护房间',
            dataIndex: 'hydrantProtected',
            key: 'hydrantProtected',
            width: 150,
            render: (text: number, record: any) => {
                const rate = record.totalRooms > 0 ? ((text / record.totalRooms) * 100).toFixed(1) : '0';
                return `${text} (${rate}%)`;
            }
        },
        { title: '甲类房间', dataIndex: 'classARooms', key: 'classARooms', width: 80 },
        { title: '乙类房间', dataIndex: 'classBRooms', key: 'classBRooms', width: 80 },
        { title: '消火栓单点保护', dataIndex: 'hydrantSingle', key: 'hydrantSingle', width: 110 },
        { title: '消火栓双点保护', dataIndex: 'hydrantDouble', key: 'hydrantDouble', width: 110 },
        { title: '疏散楼梯间', dataIndex: 'staircases', key: 'staircases', width: 100 },
        { title: '电梯间', dataIndex: 'elevatorRooms', key: 'elevatorRooms', width: 80 },
        { title: '消防电梯间', dataIndex: 'fireElevatorRooms', key: 'fireElevatorRooms', width: 100 },
        { title: '消防救援窗', dataIndex: 'rescueWindows', key: 'rescueWindows', width: 90 },
        { title: '防火窗', dataIndex: 'fireWindows', key: 'fireWindows', width: 80 },
        { title: '门总数', dataIndex: 'totalDoors', key: 'totalDoors', width: 80 },
        { title: '甲级防火门', dataIndex: 'fireDoorA', key: 'fireDoorA', width: 90 },
        { title: '乙级防火门', dataIndex: 'fireDoorB', key: 'fireDoorB', width: 90 },
        { title: '丙级防火门', dataIndex: 'fireDoorC', key: 'fireDoorC', width: 90 },
        { title: '疏散门', dataIndex: 'evacuationDoors', key: 'evacuationDoors', width: 80 },
        { title: '感烟探测器', dataIndex: 'smokeDetectors', key: 'smokeDetectors', width: 90 },
        { title: '灭火器数量', dataIndex: 'extinguisherCount', key: 'extinguisherCount', width: 90 },
        { title: '消火栓数量', dataIndex: 'hydrantCount', key: 'hydrantCount', width: 90 },
    ];

    const [tableVisible, setTableVisible] = useState(true);

    return (
        <Card
            title={<span className={styles.middleFont}>{building.building_name}</span>}
            style={{ marginBottom: 24 }}
        >
            {/* 建筑关键指标 */}
            <div style={{ marginBottom: 16, background: '#fafafa', padding: 12, borderRadius: 4 }}>
                <Row gutter={[16, 8]}>
                    <Col span={6}>总层数：地上{aboveCount}层，地下{belowCount}层</Col>
                    <Col span={6}>高度：地上{buildingInfo.building_height_above ? `${buildingInfo.building_height_above}` : '0'}m，地下{buildingInfo.building_height_below ? `${buildingInfo.building_height_below}` : '0'}m</Col>
                    <Col span={6}>耐火等级：{buildingInfo.fire_resistance_rating || '未识别'}</Col>
                    <Col span={6}>总建筑面积：{buildingInfo.total_building_area?.toFixed(2)} m²</Col>
                    <Col span={6}>人员密集场所：{summary.occupant_density.is_crowded_place}</Col>
                    <Col span={6}>房间数量：{totalRooms}</Col>
                    <Col span={6}>电梯间：{summary.elevator_rooms?.total || 0} 个{summary.elevator_rooms?.fire_elevator_rooms > 0 && `（消防电梯间${summary.elevator_rooms.fire_elevator_rooms}个）`}</Col>
                    <Col span={6}>防火分区总面积：{summary.fire_compartments.total_area.toFixed(2)} m²</Col>
                    {/*<Col span={6}>火灾自动报警系统类型：{buildingInfo.smoke_detector || '未识别'}</Col>*/}
                    <Col span={12}>灭火器保护房间：{extinguisherProtectedTotal}（覆盖率 {extinguisherCoverage}%）</Col>
                    <Col span={12}>消火栓保护房间：{hydrantProtectedTotal}（覆盖率 {hydrantCoverage}%）</Col>
                </Row>
            </div>

            {/* 展开按钮 */}
            <div style={{ textAlign: 'center', margin: '16px 0' }}>
                <Button onClick={() => setTableVisible(!tableVisible)}>
                    {tableVisible ? '收起每层详细数据' : '展开每层详细数据'}
                </Button>
            </div>

            {/* 每层详细表格 */}
            {tableVisible && (
                <div className={styles.tableWrapper}>
                    <Table
                        columns={columns}
                        dataSource={tableData}
                        pagination={false}
                        size="small"
                        scroll={{ x: 'max-content' }}
                    />
                </div>
            )}
        </Card>
    );
}

export default SummaryViewer;